// @ts-nocheck

import { createSlice } from '@reduxjs/toolkit';
import { StudyYearCategoriesState } from '@/types/study-year-category';
import { studyYearCategories } from './thunks';

const initialState: StudyYearCategoriesState = {
  isLoading: false,
  error: null,
  currentCategory: null,
  childCategories: [],
  childCategoryData: [],
  categoryData: null,
  categoryCompanies: [],
  isUpdating: false,
  // NEW: Additional state for new endpoints
  categories: [],
  categoryForecasts: [],
  parentCategory: null,
  pagination: {
    offset: 0,
    limit: 100,
    total: undefined,
  },
  filters: {},
};

export const studyYearCategoriesSlice = createSlice({
  name: 'studyYearCategories',
  initialState,
  reducers: {
    clearCurrentCategory: state => {
      state.currentCategory = null;
    },
    clearCategoryData: state => {
      state.categoryData = null;
    },
    clearChildCategories: state => {
      state.childCategories = [];
    },
    clearCategoryCompanies: state => {
      state.categoryCompanies = [];
    },
    clearError: state => {
      state.error = null;
    },
    // NEW: Additional clear actions
    clearCategories: state => {
      state.categories = [];
    },
    clearCategoryForecasts: state => {
      state.categoryForecasts = [];
    },
    clearParentCategory: state => {
      state.parentCategory = null;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: builder => {
    builder
      // NEW: getStudyYearCategories
      .addCase(studyYearCategories.getStudyYearCategories.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studyYearCategories.getStudyYearCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(studyYearCategories.getStudyYearCategories.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // NEW: getStudyYearCategoriesNew
      .addCase(studyYearCategories.getStudyYearCategoriesNew.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studyYearCategories.getStudyYearCategoriesNew.fulfilled, (state, action) => {
        state.categories = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(studyYearCategories.getStudyYearCategoriesNew.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // getStudyYearCategory
      .addCase(studyYearCategories.getStudyYearCategory.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studyYearCategories.getStudyYearCategory.fulfilled, (state, action) => {
        state.currentCategory = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(studyYearCategories.getStudyYearCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // updateStudyYearCategory
      .addCase(studyYearCategories.updateStudyYearCategory.pending, state => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(studyYearCategories.updateStudyYearCategory.fulfilled, (state, action) => {
        // Update the current category with the response data
        if (state.currentCategory) {
          state.currentCategory = { ...state.currentCategory, ...action.payload };
        }
        // Also update in categories list if it exists
        const categoryIndex = state.categories.findIndex(
          cat => cat.study_year_category_id === action.payload.study_year_category_id
        );
        if (categoryIndex !== -1) {
          state.categories[categoryIndex] = {
            ...state.categories[categoryIndex],
            ...action.payload,
          };
        }
        state.isUpdating = false;
        state.error = null;
      })
      .addCase(studyYearCategories.updateStudyYearCategory.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })

      // NEW: getStudyYearCategoryForecasts
      .addCase(studyYearCategories.getStudyYearCategoryForecasts.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studyYearCategories.getStudyYearCategoryForecasts.fulfilled, (state, action) => {
        state.categoryForecasts = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(studyYearCategories.getStudyYearCategoryForecasts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // getStudyYearCategoryData
      .addCase(studyYearCategories.getStudyYearCategoryData.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studyYearCategories.getStudyYearCategoryData.fulfilled, (state, action) => {
        state.categoryData = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(studyYearCategories.getStudyYearCategoryData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // getChildCategories
      .addCase(studyYearCategories.getChildCategories.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studyYearCategories.getChildCategories.fulfilled, (state, action) => {
        state.childCategories = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(studyYearCategories.getChildCategories.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // addChildCategory
      .addCase(studyYearCategories.addChildCategory.pending, state => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(studyYearCategories.addChildCategory.fulfilled, state => {
        state.isUpdating = false;
        state.error = null;
      })
      .addCase(studyYearCategories.addChildCategory.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })

      // NEW: getParentCategory
      .addCase(studyYearCategories.getParentCategory.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studyYearCategories.getParentCategory.fulfilled, (state, action) => {
        state.parentCategory = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(studyYearCategories.getParentCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // getChildCategoriesData
      
      .addCase(studyYearCategories.getChildCategoriesData.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studyYearCategories.getChildCategoriesData.fulfilled, (state, action) => {
        state.childCategoryData = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(studyYearCategories.getChildCategoriesData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // NEW: deleteChildCategory
      .addCase(studyYearCategories.deleteChildCategory.pending, state => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(studyYearCategories.deleteChildCategory.fulfilled, state => {
        state.isUpdating = false;
        state.error = null;
        // Note: You might want to refresh child categories after deletion
      })
      .addCase(studyYearCategories.deleteChildCategory.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })

      // getCategoryCompanies
      .addCase(studyYearCategories.getCategoryCompanies.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studyYearCategories.getCategoryCompanies.fulfilled, (state, action) => {
        state.categoryCompanies = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(studyYearCategories.getCategoryCompanies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // updateCategoryForecasts
      .addCase(studyYearCategories.updateCategoryForecasts.pending, state => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(studyYearCategories.updateCategoryForecasts.fulfilled, state => {
        state.isUpdating = false;
        state.error = null;
      })
      .addCase(studyYearCategories.updateCategoryForecasts.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearCurrentCategory,
  clearCategoryData,
  clearChildCategories,
  clearCategoryCompanies,
  clearError,
  clearCategories,
  clearCategoryForecasts,
  clearParentCategory,
  setFilters,
  setPagination,
} = studyYearCategoriesSlice.actions;

export const studyYearCategoriesReducer = studyYearCategoriesSlice.reducer;
